import * as actions from './Actions';

// Function to add UI-specific properties to the API skill object without transforming its structure
export const enhanceSkill = (payload) => {
    if (!payload) return null;
    
    // Just log the incoming payload for debugging
    console.log('API skill payload:', payload);
    
    // Create skill level descriptions object for UI components
    const skillLevelDescriptions = {
        Awareness: payload.awareness_description || '',
        Application: payload.skilled_application_description || '',
        Management: payload.management_description || ''
    };
    
    // Flag to indicate if we have any skill level descriptions
    const hasSkillLevels = Object.values(skillLevelDescriptions).some(desc => desc !== '');
    
    // Create a new object with the original payload and additional UI properties
    // This preserves the original API structure while adding necessary UI fields
    return {
        ...payload, // Keep all original API properties intact
        
        // Add UI-specific properties
        skillLevelDescriptions,
        hasSkillLevels,
        awarenessDescription: payload.awareness_description || '',
        skilledApplicationDescription: payload.skilled_application_description || '',
        managementDescription: payload.management_description || '',
        
        // Add the compressed/grouped descriptions for SkillCard
        skillLevelDescriptions: skillLevelDescriptions,
        hasSkillLevels: hasSkillLevels,
        skillLevels: hasSkillLevels ? Object.keys(skillLevelDescriptions).filter(key => 
            skillLevelDescriptions[key] !== '') : [],
    };
};

const initialState = [];

const skillsReducer = (state = initialState, action) => {
    switch (action.type) {
        case actions.INITIALIZE_SKILLS:
            return action.payload.map(skill => enhanceSkill(skill));
        case actions.ADD_SKILL:
            // For newly added skills, create a completely new object to avoid mutations
            const newSkill = action.payload;
            const existingSkills = [...state];
            
            // Create a fully hydrated skill object
            let enhancedSkill = null;
            
            // Check if the newSkill already has a complete unit object with discipline
            if (newSkill.unit && newSkill.unit.discipline) {
                // If it already has a complete unit with discipline, just enhance it
                console.log('New skill already has complete unit data with discipline:', newSkill.unit);
                enhancedSkill = enhanceSkill(newSkill);
            }
            // Check if the newSkill has a unit object but no discipline
            else if (newSkill.unit && !newSkill.unit.discipline) {
                // Try to find a matching unit with discipline data
                const matchingSkill = existingSkills.find(skill => 
                    skill.unit && skill.unit.discipline && 
                    (skill.unit.unit_id === newSkill.unit.unit_id || skill.unit.id === newSkill.unit.unit_id)
                );
                
                if (matchingSkill && matchingSkill.unit && matchingSkill.unit.discipline) {
                    // Create a new skill with the complete unit including discipline
                    console.log('Found matching unit with discipline:', matchingSkill.unit);
                    const skillWithCompleteUnit = {
                        ...newSkill,
                        unit: { 
                            ...newSkill.unit,
                            discipline: { ...matchingSkill.unit.discipline }
                        }
                    };
                    enhancedSkill = enhanceSkill(skillWithCompleteUnit);
                } else {
                    // No matching unit with discipline found, use what we have
                    enhancedSkill = enhanceSkill(newSkill);
                }
            }
            // Fall back to unit_id lookup if no unit object
            else if (newSkill.unit_id) {
                // Look for an existing skill with the same unit_id to get unit & discipline data
                const matchingSkill = existingSkills.find(skill => 
                    skill.unit && (skill.unit.unit_id === newSkill.unit_id || skill.unit.id === newSkill.unit_id)
                );
                
                if (matchingSkill && matchingSkill.unit) {
                    // Create a new skill object with the unit copied from matching skill
                    console.log('Found matching unit data for new skill:', matchingSkill.unit);
                    const skillWithUnit = {
                        ...newSkill,
                        unit: { ...matchingSkill.unit }
                    };
                    enhancedSkill = enhanceSkill(skillWithUnit);
                } else {
                    // No matching unit found, create with minimal unit object
                    console.log('No matching unit found for unit_id:', newSkill.unit_id);
                    const skillWithMinimalUnit = {
                        ...newSkill,
                        unit: { unit_id: newSkill.unit_id }
                    };
                    enhancedSkill = enhanceSkill(skillWithMinimalUnit);
                }
            } else {
                // No unit_id, just enhance as is
                enhancedSkill = enhanceSkill(newSkill);
            }
            
            console.log('Enhanced skill being added to store:', enhancedSkill);
            return [...state, enhancedSkill];
        case actions.DELETE_SKILL:
            // Normalize ids to strings and compare against both possible id fields
            return state.filter(skill => {
                const currentId = skill?.id ?? skill?.skill_id;
                return String(currentId) !== String(action.payload);
            });
        case actions.CLEAR_SKILLS_LIST:
            return [];
            
        default:
            return state;
    }
};

export default skillsReducer;
